/**
 * IF02095 get_generic_task_screen の型・定数・正規化（SR-016, SR-090〜105） データソース: DataCreateTask / GenericDataKind(AppMaster) /
 * GenericStatusManagement / GenericUploadFile(EntityType=UploadFile) / AccountingUploadTask（SR-100〜105）
 */
import * as z from 'zod/mini'

// --- 定数: 期間・確定状態（SR-091, SR-093）---

/** @type {'monthly'} */
export const PERIOD_TYPE_MONTHLY = 'monthly'
/** @type {'quarter'} */
export const PERIOD_TYPE_QUARTER = 'quarter'
/** @type {'year'} */
export const PERIOD_TYPE_YEAR = 'year'

/** @type {'pending'} */
export const DATA_FIX_STATUS_PENDING = 'pending'
/** @type {'fix'} */
export const DATA_FIX_STATUS_FIX = 'fix'

// --- 定数: GenericStatusManagement.Status（SR-017, SR-097, chrofy_layer GenericStatusManagementStatusEnum）---

/** @type {'unset'} */
export const GENERIC_STATUS_UNSET = 'unset'
/** @type {'configuration-pending'} */
export const GENERIC_STATUS_CONFIGURATION_PENDING = 'configuration-pending'
/** @type {'configuring'} */
export const GENERIC_STATUS_CONFIGURING = 'configuring'
/** @type {'verification-pending'} */
export const GENERIC_STATUS_PENDING_VERIFICATION = 'verification-pending'
/** @type {'verifying'} */
export const GENERIC_STATUS_VERIFYING = 'verifying'
/** @type {'verified'} */
export const GENERIC_STATUS_VERIFIED = 'verified'
/** @type {'verification-error'} */
export const GENERIC_STATUS_VERIFICATION_ERROR = 'verification-error'
/** @type {'import-pending'} */
export const GENERIC_STATUS_IMPORT_PENDING = 'import-pending'
/** @type {'reimport-pending'} */
export const GENERIC_STATUS_REIMPORT_PENDING = 'reimport-pending'
/** @type {'imported'} */
export const GENERIC_STATUS_IMPORTED = 'imported'

/** @type {'GDK-0001'} */
export const GENERIC_DATA_KIND_PERSONAL = 'GDK-0001'

/** @type {'GDK-0030'} */
export const GENERIC_DATA_KIND_GENERIC_ITEM = 'GDK-0030'

/** Status_by_data_kind の値として想定する列挙（API は .value 文字列を返す） 画面設計 SR-017 に加え、レイヤー Enum の configuration-pending / verified を含む */
export const GENERIC_DATA_KIND_STATUS_VALUES = [
  GENERIC_STATUS_UNSET,
  GENERIC_STATUS_CONFIGURATION_PENDING,
  GENERIC_STATUS_CONFIGURING,
  GENERIC_STATUS_PENDING_VERIFICATION,
  GENERIC_STATUS_VERIFYING,
  GENERIC_STATUS_VERIFIED,
  GENERIC_STATUS_VERIFICATION_ERROR,
  GENERIC_STATUS_IMPORT_PENDING,
  GENERIC_STATUS_REIMPORT_PENDING,
  GENERIC_STATUS_IMPORTED
]

export const GenericDataKindStatusSchema = z.enum(GENERIC_DATA_KIND_STATUS_VALUES)

/** @typedef {import('zod/mini').infer<typeof GenericDataKindStatusSchema>} GenericDataKindStatus */

// --- 定数: 財務アップロード行（SR-098, get_generic_task_screen map_accounting_upload_tasks）---

/** API レスポンスの target（Mode を 単体/連結 ラベルに変換済み） */
export const ACCOUNTING_TARGET_STANDALONE_LABEL = '単体'
export const ACCOUNTING_TARGET_CONSOLIDATED_LABEL = '連結'

/** @type {'unregistered'} */
export const ACCOUNTING_ROW_STATUS_UNREGISTERED = 'unregistered'
/** @type {'registered'} */
export const ACCOUNTING_ROW_STATUS_REGISTERED = 'registered'
/** @type {'processing'} */
export const ACCOUNTING_ROW_STATUS_PROCESSING = 'processing'
/** @type {'error'} */
export const ACCOUNTING_ROW_STATUS_ERROR = 'error'

export const AccountingUploadTargetSchema = z.enum([
  ACCOUNTING_TARGET_STANDALONE_LABEL,
  ACCOUNTING_TARGET_CONSOLIDATED_LABEL
])

export const AccountingRowStatusSchema = z.enum([
  ACCOUNTING_ROW_STATUS_UNREGISTERED,
  ACCOUNTING_ROW_STATUS_REGISTERED,
  ACCOUNTING_ROW_STATUS_PROCESSING,
  ACCOUNTING_ROW_STATUS_ERROR
])

// --- GenericUploadFile.UploadFileStatusEnum（chrofy_layer generic_upload_file）API は .value 文字列 ---

/** @type {'pending-virus-check'} */
export const GENERIC_UPLOAD_FILE_STATUS_PENDING_VIRUS_CHECK = 'pending-virus-check'
/** @type {'virus-checking'} */
export const GENERIC_UPLOAD_FILE_STATUS_VIRUS_CHECKING = 'virus-checking'
/** @type {'pending-conversion'} */
export const GENERIC_UPLOAD_FILE_STATUS_PENDING_CONVERSION = 'pending-conversion'
/** @type {'uploaded'} */
export const GENERIC_UPLOAD_FILE_STATUS_UPLOADED = 'uploaded'
/** @type {'pending-deletion'} */
export const GENERIC_UPLOAD_FILE_STATUS_PENDING_DELETION = 'pending-deletion'
/** @type {'virus-check-error'} */
export const GENERIC_UPLOAD_FILE_STATUS_VIRUS_CHECK_ERROR = 'virus-check-error'
/** @type {'conversion-error'} */
export const GENERIC_UPLOAD_FILE_STATUS_CONVERSION_ERROR = 'conversion-error'
/** @type {'validation-error'} */
export const GENERIC_UPLOAD_FILE_STATUS_VALIDATION_ERROR = 'validation-error'
/** @type {'unexpected-error'} */
export const GENERIC_UPLOAD_FILE_STATUS_UNEXPECTED_ERROR = 'unexpected-error'

export const GENERIC_UPLOAD_FILE_STATUS_VALUES = [
  GENERIC_UPLOAD_FILE_STATUS_PENDING_VIRUS_CHECK,
  GENERIC_UPLOAD_FILE_STATUS_VIRUS_CHECKING,
  GENERIC_UPLOAD_FILE_STATUS_PENDING_CONVERSION,
  GENERIC_UPLOAD_FILE_STATUS_UPLOADED,
  GENERIC_UPLOAD_FILE_STATUS_PENDING_DELETION,
  GENERIC_UPLOAD_FILE_STATUS_VIRUS_CHECK_ERROR,
  GENERIC_UPLOAD_FILE_STATUS_CONVERSION_ERROR,
  GENERIC_UPLOAD_FILE_STATUS_VALIDATION_ERROR,
  GENERIC_UPLOAD_FILE_STATUS_UNEXPECTED_ERROR
]

export const GenericUploadFileStatusSchema = z.enum(GENERIC_UPLOAD_FILE_STATUS_VALUES)

/** @typedef {import('zod/mini').infer<typeof GenericUploadFileStatusSchema>} GenericUploadFileStatus */

// --- Zod（正規化後 camelCase・SR-090〜099）---

/** SR-091 */
export const PeriodTypeSchema = z.enum([PERIOD_TYPE_MONTHLY, PERIOD_TYPE_QUARTER, PERIOD_TYPE_YEAR])

/** @typedef {import('zod/mini').infer<typeof PeriodTypeSchema>} PeriodType */

/** SR-093 */
export const DataFixStatusSchema = z.enum([DATA_FIX_STATUS_PENDING, DATA_FIX_STATUS_FIX])

/** @typedef {import('zod/mini').infer<typeof DataFixStatusSchema>} DataFixStatus */

/** SR-094 汎用タスクステータス（マスタ定義の文字列） */
export const DataTaskStatusSchema = z.string()

/** SR-095 operation_history 要素（permit は null 可） */
export const OperationHistoryItemSchema = z.object({
  operationKind: z.string(),
  operationUser: z.string(),
  operationStartDateTime: z.string(),
  operationEndDateTime: z.string(),
  permitUser: z.nullish(z.string()),
  permitDateTime: z.nullish(z.string())
})

/** @typedef {import('zod/mini').infer<typeof OperationHistoryItemSchema>} OperationHistoryItem */

/** SR-090 data_create_task 集約ブロック */
export const DataCreateTaskScreenSliceSchema = z.object({
  periodType: PeriodTypeSchema,
  periodLabel: z.string(),
  dataFixStatus: DataFixStatusSchema,
  dataTaskStatus: DataTaskStatusSchema,
  isBaseFileSet: z._default(z.optional(z.boolean()), false),
  /** 四半期/年次タスクで、対象期間の最終月にあたる月次タスクが更新済以上 （created または fix）か（IF02095 拡張）。true のときファイル無しでもデータ更新可。 月次タスクでは常に false。 */
  closestMonthlyDataCreated: z._default(z.optional(z.boolean()), false),
  operationHistory: z.array(OperationHistoryItemSchema)
})

/** @typedef {import('zod/mini').infer<typeof DataCreateTaskScreenSliceSchema>} DataCreateTaskScreenSlice */

/** SR-096 generic_data_kinds 要素（has_permission は含めない） */
export const GenericDataKindItemSchema = z.object({
  genericDataKindCode: z.string(),
  genericDataKindName: z.string(),
  genericDataSuperKindCode: z.string(),
  genericDataSuperKindName: z.string(),
  // ベース変更・削除時にステータスが「検証待ち」へ巻き戻る分類か（IF02095、バグ#3233）
  isBaseResetTarget: z._default(z.optional(z.boolean()), false)
})

/** @typedef {import('zod/mini').infer<typeof GenericDataKindItemSchema>} GenericDataKindItem */

/** SR-097 キー=データ分類コード、値=GenericStatusManagement.Status */
export const StatusByDataKindSchema = z.record(z.string(), z.string())

/** SR-097 拡張: キー=データ分類コード、値=ステータス変更者・変更日時メタ。 IF02095 が GenericStatusManagement の UpdateUserName / UpdateDateTime を返す。 */
export const StatusMetaByDataKindItemSchema = z.object({
  updateUserName: z._default(z.optional(z.string()), ''),
  updateDateTime: z._default(z.optional(z.string()), '')
})

export const StatusMetaByDataKindSchema = z.record(z.string(), StatusMetaByDataKindItemSchema)

/** @typedef {import('zod/mini').infer<typeof StatusMetaByDataKindItemSchema>} StatusMetaByDataKindItem */

/** SR-098 target は API 上 単体/連結。status は AccountingUploadStatusEnum の値 */
export const AccountingUploadTaskItemSchema = z.object({
  target: AccountingUploadTargetSchema,
  mode: z.optional(z.string()),
  fileName: z.string(),
  registeredBy: z.string(),
  registeredAt: z.string(),
  subjectCount: z.coerce.number(),
  status: AccountingRowStatusSchema,
  errorInfo: z._default(z.optional(z.string()), ''),
  /** 財務データ登録画面 URL 用（API が key / upload_task_key で返す場合） */
  uploadTaskKey: z._default(z.optional(z.string()), '')
})

/** @typedef {import('zod/mini').infer<typeof AccountingUploadTaskItemSchema>} AccountingUploadTaskItem */

/** SR-099 GenericUploadFile 由来（file_size 等は IF02095 拡張） */
export const UploadFileListItemSchema = z.object({
  fileName: z._default(z.optional(z.string()), ''),
  dataClassification: z._default(z.optional(z.string()), ''),
  updateDateTime: z._default(z.optional(z.string()), ''),
  fileSize: z._default(z.optional(z.coerce.number()), 0),
  rowCount: z._default(z.optional(z.coerce.number()), 0),
  lastUpdateUserName: z._default(z.optional(z.string()), ''),
  status: z._default(z.optional(z.union([GenericUploadFileStatusSchema, z.string()])), '')
})

/** @typedef {import('zod/mini').infer<typeof UploadFileListItemSchema>} UploadFileListItem */

/** IF02095 正規化済みレスポンス全体 */
export const GenericTaskScreenResponseSchema = z.object({
  dataCreateTask: DataCreateTaskScreenSliceSchema,
  genericDataKinds: z.array(GenericDataKindItemSchema),
  statusByDataKind: StatusByDataKindSchema,
  statusMetaByDataKind: StatusMetaByDataKindSchema,
  accountingUploadTasks: z.array(AccountingUploadTaskItemSchema),
  uploadFileList: z.array(UploadFileListItemSchema)
})

/** @typedef {import('zod/mini').infer<typeof GenericTaskScreenResponseSchema>} GenericTaskScreenNormalized */

// --- 正規化（SR-016 snake_case → camelCase）---

/**
 * POST 等で返る JSON のキーを snake_case → camelCase に揃える（api-patterns: ストア・画面は camelCase）。 `deep: false` のときは 1
 * オブジェクト分のみ変換し配下はそのまま（IF02095 部分正規化用）。
 *
 * @param {unknown} raw
 * @param {{ deep?: boolean }} [options]
 * @returns {unknown}
 */
export function normalizeDeepKeysToCamel(raw, options) {
  const deep = options?.deep !== false
  if (raw === null || raw === undefined) {
    return raw
  }
  if (Array.isArray(raw)) {
    return deep ? raw.map((item) => normalizeDeepKeysToCamel(item, options)) : raw
  }
  if (typeof raw !== 'object') {
    return raw
  }
  const obj = /** @type {Record<string, unknown>} */ (raw)
  const out = {}
  for (const [k, v] of Object.entries(obj)) {
    const camel = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
    out[camel] = deep ? normalizeDeepKeysToCamel(v, options) : v
  }
  return out
}

/**
 * Create_data / fix_* 等、スキーマ固定でない POST 応答を zod で通過させる（api-patterns §2: services は検証済みを返す）。
 *
 * @param {unknown} raw
 * @returns {unknown}
 */
export function parseLooseApiResponseBody(raw) {
  const camel = normalizeDeepKeysToCamel(raw)
  return z.unknown().parse(camel)
}

/**
 * 財務「対象」表示用: API の standalone/consolidated または既存ラベルを画面用に正規化（SR-011）
 *
 * @param {string | null | undefined} target
 * @returns {string}
 */
export function normalizeAccountingTargetForDisplay(target) {
  if (target == null || target === '') {
    return ''
  }
  const s = String(target).trim()
  if (s === 'standalone') {
    return ACCOUNTING_TARGET_STANDALONE_LABEL
  }
  if (s === 'consolidated') {
    return ACCOUNTING_TARGET_CONSOLIDATED_LABEL
  }
  if (s === ACCOUNTING_TARGET_STANDALONE_LABEL || s === ACCOUNTING_TARGET_CONSOLIDATED_LABEL) {
    return s
  }
  return s
}

/**
 * 財務行の省略フィールドを正規化スキーマ向けに補完する
 *
 * @param {Record<string, unknown>} row
 * @returns {Record<string, unknown>}
 */
function _normalizeAccountingRow(row) {
  const o = { ...row }
  if (o.errorInfo === undefined && o.error_info !== undefined) {
    o.errorInfo = o.error_info
    delete o.error_info
  }
  if (o.errorInfo === undefined || o.errorInfo === null) {
    o.errorInfo = ''
  }
  o.target = normalizeAccountingTargetForDisplay(o.target != null ? String(o.target) : '')
  if (o.uploadTaskKey === undefined || o.uploadTaskKey === '') {
    const rawKey = o.key ?? o.upload_task_key ?? o.uploadTaskKey
    if (rawKey != null && String(rawKey).trim() !== '') {
      o.uploadTaskKey = String(rawKey)
    }
  }
  return o
}

/**
 * IF02095 生レスポンスを camelCase にし Zod で検証する（SR-016） status_by_data_kind のキーはデータ分類コードのため変換しない（SR-097）
 *
 * @param {unknown} raw HttpGet の body（snake_case 想定、一部既に camel の場合も許容）
 * @returns {GenericTaskScreenNormalized}
 */
export function normalizeGenericTaskScreenResponse(raw) {
  const body = /** @type {Record<string, unknown>} */ (raw && typeof raw === 'object' ? raw : {})
  const rootLayer = /** @type {Record<string, unknown>} */ (normalizeDeepKeysToCamel(body, { deep: false }))

  const dctIn = /** @type {Record<string, unknown>} */ (rootLayer.dataCreateTask ?? body.data_create_task ?? {})
  const dct = /** @type {Record<string, unknown>} */ (normalizeDeepKeysToCamel(dctIn, { deep: false }))
  const opSrc = dct.operationHistory ?? dctIn.operation_history
  const operationHistory = Array.isArray(opSrc)
    ? opSrc.map((row) => /** @type {Record<string, unknown>} */ (normalizeDeepKeysToCamel(row, { deep: false })))
    : []

  const dataCreateTask = {
    periodType: String(dct.periodType ?? dctIn.period_type ?? '').trim(),
    periodLabel: String(dct.periodLabel ?? dctIn.period_label ?? ''),
    dataFixStatus: String(dct.dataFixStatus ?? dctIn.data_fix_status ?? '').trim(),
    dataTaskStatus: String(dct.dataTaskStatus ?? dctIn.data_task_status ?? ''),
    isBaseFileSet: Boolean(dct.isBaseFileSet ?? dctIn.is_base_file_set ?? false),
    closestMonthlyDataCreated: Boolean(dct.closestMonthlyDataCreated ?? dctIn.closest_monthly_data_created ?? false),
    operationHistory
  }

  const kindsSrc = rootLayer.genericDataKinds ?? body.generic_data_kinds
  const genericDataKinds = Array.isArray(kindsSrc)
    ? kindsSrc
        .map((row) => /** @type {Record<string, unknown>} */ (normalizeDeepKeysToCamel(row, { deep: false })))
        .filter(
          (row) =>
            String(row.genericDataKindCode ?? '')
              .trim()
              .toLowerCase() !== 'none'
        )
    : []

  const statusSrc = rootLayer.statusByDataKind ?? body.status_by_data_kind
  const statusByDataKind =
    statusSrc && typeof statusSrc === 'object' && !Array.isArray(statusSrc)
      ? { .../** @type {Record<string, string>} */ (statusSrc) }
      : {}

  const statusMetaSrc = rootLayer.statusMetaByDataKind ?? body.status_meta_by_data_kind
  const statusMetaByDataKind =
    statusMetaSrc && typeof statusMetaSrc === 'object' && !Array.isArray(statusMetaSrc)
      ? Object.fromEntries(
          Object.entries(/** @type {Record<string, unknown>} */ (statusMetaSrc)).map(([code, meta]) => {
            const m = /** @type {Record<string, unknown>} */ (normalizeDeepKeysToCamel(meta ?? {}, { deep: false }))
            return [
              code,
              {
                updateUserName: String(m.updateUserName ?? ''),
                updateDateTime: String(m.updateDateTime ?? '')
              }
            ]
          })
        )
      : {}

  const accSrc = rootLayer.accountingUploadTasks ?? body.accounting_upload_tasks
  const accountingUploadTasks = Array.isArray(accSrc)
    ? accSrc.map((row) =>
        _normalizeAccountingRow(/** @type {Record<string, unknown>} */ (normalizeDeepKeysToCamel(row, { deep: false })))
      )
    : []

  const uploadSrc = rootLayer.uploadFileList ?? body.upload_file_list
  const uploadFileList = Array.isArray(uploadSrc)
    ? uploadSrc
        .map((row) => /** @type {Record<string, unknown>} */ (normalizeDeepKeysToCamel(row, { deep: false })))
        .filter((row) => String(row.entityType ?? '').trim() !== 'FileClassification')
        .map((row) => {
          const o = { ...row }
          for (const k of ['fileName', 'dataClassification', 'updateDateTime', 'lastUpdateUserName']) {
            if (o[k] == null) o[k] = ''
          }
          return o
        })
    : []

  const normalized = {
    dataCreateTask,
    genericDataKinds,
    statusByDataKind,
    statusMetaByDataKind,
    accountingUploadTasks,
    uploadFileList
  }

  return GenericTaskScreenResponseSchema.parse(normalized)
}

// --- マッピング設定 Excel ダウンロード用（IF02090 / IF02085 / IF02092）---

/** IF02090 get_all_chrofy_columns: 項目 1 件 */
export const ChrofyColumnItemSchema = z.looseObject({
  column_code: z._default(z.optional(z.string()), ''),
  column_name: z._default(z.optional(z.string()), ''),
  column_data_type: z._default(z.optional(z.nullable(z.string())), ''),
  /** 対象四半期（例: ["2Q", "4Q"]）。未設定なら全期間対象 */
  target_quarter: z._default(z.optional(z.nullable(z.array(z.string()))), [])
})

/** IF02090: データ分類ごとの項目一覧 */
export const ChrofyColumnGroupSchema = z.looseObject({
  data_classification_code: z._default(z.optional(z.string()), ''),
  data_classification_name: z._default(z.optional(z.string()), ''),
  columns: z._default(z.optional(z.array(ChrofyColumnItemSchema)), [])
})

/** IF02090 レスポンス全体（データ分類の配列） */
export const AllChrofyColumnsResponseSchema = z.array(ChrofyColumnGroupSchema)

/** @typedef {import('zod/mini').infer<typeof ChrofyColumnGroupSchema>} ChrofyColumnGroup */

/** IF02085 get_generic_task_setting: マッピング 1 件（mapping_info は型ごとに構造が異なるため loose） */
export const GenericMappingSettingItemSchema = z.looseObject({
  generic_task_code: z._default(z.optional(z.string()), ''),
  chrofy_column_code: z._default(z.optional(z.string()), ''),
  priority: z._default(z.optional(z.coerce.number()), 0),
  num_agg_type: z._default(z.optional(z.string()), 'priority'),
  mapping_type: z._default(z.optional(z.string()), ''),
  mapping_info: z._default(z.optional(z.record(z.string(), z.unknown())), {})
})

/** IF02085 レスポンス全体（マッピングの配列） */
export const GenericMappingSettingResponseSchema = z.array(GenericMappingSettingItemSchema)

/** @typedef {import('zod/mini').infer<typeof GenericMappingSettingItemSchema>} GenericMappingSettingItem */

/**
 * IF02092 get_judgment_rule: 判定ルールの 1 行（条件 → 値）。
 *
 * IF02092 は `model_dump(by_alias=True)` で返すため **PascalCase**（`Condition` / `Value`）。 旧データ・将来の変更に備えて snake_case も受け付ける。
 */
export const JudgmentRuleRowSchema = z.looseObject({
  Condition: z.optional(z.nullable(z.string())),
  Value: z.optional(z.nullable(z.string())),
  condition: z.optional(z.nullable(z.string())),
  value: z.optional(z.nullable(z.string()))
})

/** IF02092: 判定ルール 1 件（PascalCase。`JudgmentRules` の最終要素は ELSE） */
export const JudgmentRuleItemSchema = z.looseObject({
  RuleName: z.optional(z.nullable(z.string())),
  DataKindCode: z.optional(z.nullable(z.string())),
  ChrofyColumnCode: z.optional(z.nullable(z.string())),
  JudgmentRules: z._default(z.optional(z.nullable(z.array(JudgmentRuleRowSchema))), [])
})

/** IF02092 レスポンス全体（判定ルールの配列） */
export const JudgmentRuleResponseSchema = z.array(JudgmentRuleItemSchema)

/** @typedef {import('zod/mini').infer<typeof JudgmentRuleItemSchema>} JudgmentRuleItem */

/** IF02102 list_conversion_tables: 変換表 1 件（Excel の変換表名解決用） */
export const ConversionTableListItemSchema = z.looseObject({
  conversion_table_key: z._default(z.optional(z.string()), ''),
  conversion_table_name: z._default(z.optional(z.nullable(z.string())), '')
})

/** IF02102 レスポンス（`conversion_table_list` にラップされる） */
export const ConversionTableListResponseSchema = z.array(ConversionTableListItemSchema)

/** @typedef {import('zod/mini').infer<typeof ConversionTableListItemSchema>} ConversionTableListItem */
